// Client AI service. Calls the server-side /api/generate function (which holds the
// Anthropic key) — Claude is NEVER called from the browser. Keeps the original
// generatePrayerGuide(params) -> { title, introduction, days } contract so the
// existing generator UI is unchanged.
import { supabase, isSupabaseConfigured } from './supabaseClient';
import { ERROR_TYPES } from '../utils/ErrorHandler';

// Map a server error "kind" to the app's ERROR_TYPES so ErrorDisplay renders it.
const KIND_TO_TYPE = {
    auth: ERROR_TYPES.VALIDATION,
    rate_limit: ERROR_TYPES.RATE_LIMIT,
    config: ERROR_TYPES.SERVER,
    refusal: ERROR_TYPES.VALIDATION,
    truncated: ERROR_TYPES.VALIDATION,
    parse: ERROR_TYPES.SERVER,
    validation: ERROR_TYPES.SERVER,
    server: ERROR_TYPES.SERVER,
    unknown: ERROR_TYPES.UNKNOWN
};

const fail = (type, message) => {
    const error = new Error(message);
    error.type = type;
    throw error;
};

/**
 * Generate structured AI content via the server.
 * @param {string} kind - prayer_plan | bible_study | tailored_verse
 * @param {object} input
 * @returns {Promise<object>} the validated content object
 */
export const generate = async (kind, input) => {
    if (!isSupabaseConfigured) {
        fail(ERROR_TYPES.SERVER, 'Accounts aren’t set up yet, so AI generation is unavailable.');
    }

    const { data: { session } = {} } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) {
        fail(ERROR_TYPES.VALIDATION, 'Please sign in to use AI generation.');
    }

    let response;
    try {
        response = await fetch('/api/generate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ kind, input })
        });
    } catch (_networkError) {
        fail(ERROR_TYPES.NETWORK, 'Network error. Please check your connection and try again.');
    }

    let payload = {};
    try {
        payload = await response.json();
    } catch (_e) { /* leave empty */ }

    if (!response.ok) {
        const type = KIND_TO_TYPE[payload.kind] || ERROR_TYPES.UNKNOWN;
        fail(type, payload.error || 'Something went wrong generating your content.');
    }

    return payload.content;
};

/**
 * Generate a personalized prayer guide. Returns { title, introduction, days[] }.
 */
export const generatePrayerGuide = async (params) => generate('prayer_plan', params);

export default { generate, generatePrayerGuide };
